import { useEffect, useRef, useState } from "react";
import { IconX, IconFileText } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { IconPill } from "../../../../components/common/icon-pill";
import { Button } from "../../../../components/common/button";
import { CreateOrderDisputeDtoReasonEnum } from "../../../../api";
import {
  createOrderDispute,
  useOrderDisputeAttachmentUpload,
  type AttachmentInputDto,
} from "../../hooks/useMyOrderDisputes";
import { DISPUTE_REASON_LABEL } from "./dispute-labels";
import { showSuccessToast } from "../../../../utils/toasts";

const REASON_OPTIONS = Object.values(CreateOrderDisputeDtoReasonEnum).map((value) => ({
  value,
  label: DISPUTE_REASON_LABEL[value] ?? value,
}));

/**
 * What a buyer can report while the seller hasn't shipped yet — the API
 * refuses the other reasons (problems with the goods) until there's a
 * shipment.
 */
const PRE_SHIPMENT_REASONS: readonly CreateOrderDisputeDtoReasonEnum[] = [
  CreateOrderDisputeDtoReasonEnum.NotDelivered,
  CreateOrderDisputeDtoReasonEnum.LateDelivery,
  CreateOrderDisputeDtoReasonEnum.BillingIssue,
];
const PRE_SHIPMENT_REASON_OPTIONS = REASON_OPTIONS.filter((o) =>
  PRE_SHIPMENT_REASONS.includes(o.value),
);

interface ReportDisputeModalProps {
  isOpen: boolean;
  saleOrderId: number;
  orderName: string;
  /** False while the seller hasn't shipped: only pre-shipment reasons are offered. */
  hasShipped: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ReportDisputeModal({
  isOpen,
  saleOrderId,
  orderName,
  hasShipped,
  onClose,
  onSuccess,
}: ReportDisputeModalProps) {
  const [reason, setReason] = useState<CreateOrderDisputeDtoReasonEnum | "">("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { isUploading, upload } = useOrderDisputeAttachmentUpload();

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setDescription("");
    setFile(null);
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  }

  const canSubmit = !!reason && description.trim().length > 0 && !submitting && !isUploading;

  async function handleSubmit() {
    if (!reason || !description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      let attachments: AttachmentInputDto[] | undefined;
      if (file) {
        const uploaded = await upload(file);
        if (!uploaded) {
          setError("Failed to upload attachment. Please try again.");
          return;
        }
        attachments = [uploaded];
      }
      const created = await createOrderDispute({
        saleOrderId,
        reason,
        description: description.trim(),
        ...(attachments ? { attachments } : {}),
      });
      if (!created) {
        setError("Failed to submit your report. Please try again.");
        return;
      }
      showSuccessToast("Your issue has been reported. Our team will review it shortly.");
      onSuccess?.();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[560px] rounded-2xl bg-bgWhite p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Heading variant="h3" tone="dark">
            Report an Issue — {orderName}
          </Heading>
          <IconPill
            as="button"
            size={32}
            tone="ghost"
            aria-label="Close"
            onClick={onClose}
            className="!text-textMuted hover:!bg-bgLight hover:!text-textDark"
          >
            <IconX size={18} />
          </IconPill>
        </div>

        <div className="my-5 border-t border-borderLight" />

        <div className="flex flex-col gap-5">
          <Select
            name="reason"
            label="What went wrong?"
            placeholder="Select a reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as CreateOrderDisputeDtoReasonEnum)}
            options={hasShipped ? REASON_OPTIONS : PRE_SHIPMENT_REASON_OPTIONS}
          />

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">
              Describe the issue
            </Text>
            <Input
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us what happened so we can help resolve it…"
              className="placeholder:!text-textMuted"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">
              Attachment (optional)
            </Text>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              className={`flex min-h-[90px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed transition-colors ${
                dragging ? "border-accentCyan bg-accentCyanTint" : "border-borderDark bg-bgLight"
              }`}
            >
              {!file ? (
                <Text variant="bodySm" tone="muted">
                  Drag & drop a photo here or click to browse &nbsp;(JPG, PNG, PDF)
                </Text>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <IconFileText size={18} className="text-textMuted" />
                  <Text variant="bodySm" tone="muted" className="line-clamp-1">
                    {file.name}
                  </Text>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            {file && (
              <button type="button" onClick={() => setFile(null)} className="self-start">
                <Text variant="bodyXs" tone="muted" className="underline">
                  Remove attachment
                </Text>
              </button>
            )}
          </div>

          {error && (
            <Text variant="bodyXs" className="text-accentError">
              {error}
            </Text>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Button variant="primary" size="md" fullWidth disabled={!canSubmit} onClick={handleSubmit}>
            {submitting || isUploading ? "Submitting…" : "Submit Report"}
          </Button>
          <Button variant="secondary" size="md" fullWidth onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
